import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Save, Video, Bell, Gift, DollarSign, ArrowLeft, Info } from 'lucide-react';
import Navbar from '../components/Navbar';

const SettingsPage = ({ user, onLogout, refreshUserData }) => {
  const navigate = useNavigate();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Settings state
  const [settings, setSettings] = useState({
    youtubeVideoUrl: '',
    emailNotifications: true,
    smsNotifications: false,
    referralType: 'fixed', // 'fixed', 'first_transaction', 'promo_code'
    referralBonusAmount: 10,
    transfer_fee: 24.95, // Fixed transfer fee
    stripeConnected: false,
    twilioConfigured: false
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.settings) {
          setSettings(prevSettings => ({
            ...prevSettings,
            ...data.settings,
            // Use 24.95 as default if not set
            transfer_fee: data.settings.transfer_fee || 24.95
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ settings })
      });

      if (response.ok) {
        setMessage('Paramètres sauvegardés avec succès!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      setMessage('Erreur: ' + error.message);
    }
    setLoading(false);
  };

  if (!user || user.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-2">Accès refusé</h2>
          <p className="text-gray-600">Seuls les super administrateurs peuvent accéder aux paramètres de la plateforme.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
      <div className="h-20"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 max-w-7xl">
        <div className="mb-4 sm:mb-6">
          <Button
            onClick={() => navigate('/admin')}
            variant="ghost"
            className="mb-2 sm:mb-4"
            size="sm"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Retour au tableau de bord</span>
            <span className="sm:hidden">Retour</span>
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Paramètres de la plateforme</h1>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.includes('succès') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {message}
          </div>
        )}

        <div className="space-y-6">
          {/* YouTube Video Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Video className="mr-2 h-5 w-5 text-brand-600" />
                Vidéo elevator pitch
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL YouTube (vidéo de 15 secondes)
                  </label>
                  <Input
                    type="text"
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={settings.youtubeVideoUrl}
                    onChange={(e) => setSettings({ ...settings, youtubeVideoUrl: e.target.value })}
                  />
                  <p className="text-sm text-gray-500 mt-2">
                    La vidéo sera affichée en 2e section de la page d'accueil
                  </p>
                </div>
                {settings.youtubeVideoUrl && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Aperçu:</p>
                    <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                      <p className="text-gray-500">Vidéo YouTube sera affichée ici</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Notifications Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Bell className="mr-2 h-5 w-5 text-brand-600" />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Notifications par courriel</p>
                    <p className="text-sm text-gray-500">Activer les notifications email</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.emailNotifications}
                    onChange={(e) => setSettings({ ...settings, emailNotifications: e.target.checked })}
                    className="h-5 w-5 text-brand-600"
                  />
                </div>
                
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium">Notifications SMS/Textos</p>
                      <p className="text-sm text-gray-500">Configuration Twilio requise</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={settings.smsNotifications}
                      onChange={(e) => setSettings({ ...settings, smsNotifications: e.target.checked })}
                      className="h-5 w-5 text-brand-600"
                      disabled={!settings.twilioConfigured}
                    />
                  </div>
                  {!settings.twilioConfigured && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                      <p className="text-sm text-yellow-800">
                        ⚠️ Configuration Twilio non disponible. Les SMS seront configurés ultérieurement.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Referral Program Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Gift className="mr-2 h-5 w-5 text-brand-600" />
                Programme de parrainage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de bonus
                  </label>
                  <select
                    value={settings.referralType}
                    onChange={(e) => setSettings({ ...settings, referralType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="fixed">Montant fixe immédiat</option>
                    <option value="first_transaction">Après 1ère transaction du filleul</option>
                    <option value="promo_code">Code promo</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Montant du bonus ($)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={settings.referralBonusAmount}
                    onChange={(e) => setSettings({ ...settings, referralBonusAmount: parseFloat(e.target.value) })}
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Type sélectionné:</strong> {
                      settings.referralType === 'fixed' ? 'Les deux parties reçoivent le bonus immédiatement' :
                      settings.referralType === 'first_transaction' ? 'Bonus après la première transaction complétée du filleul' :
                      'Système de codes promo à utiliser lors des transactions'
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fee Structure Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <DollarSign className="mr-2 h-5 w-5 text-brand-600" />
                Structure des frais de transaction
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Frais de transfert fixe ($)
                  </label>
                  <div className="max-w-xs">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={settings.transfer_fee}
                      onChange={(e) => setSettings({ ...settings, transfer_fee: parseFloat(e.target.value) || 0 })}
                      className="text-lg font-semibold"
                      data-testid="transfer-fee-input"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Ce montant sera facturé pour chaque transfert de billets
                  </p>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800 font-medium">
                      Frais actuel: {settings.transfer_fee?.toFixed(2) || '24.95'}$ par transfert
                    </p>
                    <p className="text-sm text-blue-700 mt-1">
                      Ce frais est automatiquement appliqué à toutes les nouvelles demandes de transfert.
                      Vous pouvez le modifier à tout moment et les nouvelles transactions utiliseront le nouveau montant.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stripe Connect Section - TEMPORAIREMENT MASQUÉ (Interac uniquement) */}
          {/* <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <DollarSign className="mr-2 h-5 w-5 text-brand-600" />
                Configuration Stripe Connect
              </CardTitle>
            </CardHeader>
            <CardContent>
              {settings.stripeConnected ? (
                <div className="bg-green-50 border border-green-200 rounded p-4">
                  <div className="flex items-center mb-2">
                    <svg className="h-5 w-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm font-semibold text-green-800">
                      ✓ Stripe Connect est configuré et opérationnel
                    </p>
                  </div>
                  <p className="text-sm text-green-700 ml-7">
                    Les vendeurs peuvent maintenant connecter leurs comptes Stripe pour recevoir les paiements. Le système de transfert sécurisé et de split payments est actif.
                  </p>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                  <p className="text-sm text-yellow-800 mb-3">
                    ⚠️ Stripe Connect n'est pas encore configuré. Cette fonctionnalité sera implémentée prochainement pour gérer les paiements sécurisés.
                  </p>
                  <Button disabled variant="outline">
                    Configurer Stripe Connect
                  </Button>
                </div>
              )}
            </CardContent>
          </Card> */}

          {/* Save Button */}
          <div className="flex justify-end sticky bottom-4 sm:static">
            <Button
              onClick={saveSettings}
              disabled={loading}
              className="bg-brand-600 hover:bg-brand-700 text-white w-full sm:w-auto shadow-lg sm:shadow-none"
            >
              <Save className="mr-2 h-4 w-4" />
              {loading ? 'Sauvegarde...' : 'Sauvegarder les paramètres'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
